import { EventEmitter, Injectable, inject, DOCUMENT } from '@angular/core';
import { StorageService } from './storage.service';
import { AuthApiService } from './api/auth-api.service';
import { CLAIMS } from '../constants/auth-constants';

export type AppTheme = 'dark' | 'light';

@Injectable({
  providedIn: 'root',
})
export class ThemeService {
  private document = inject<Document>(DOCUMENT);
  private storageService = inject(StorageService);
  private authApi = inject(AuthApiService);

  themeChanged: EventEmitter<AppTheme> = new EventEmitter();
  currentTheme!: AppTheme;

  switchTheme(theme: AppTheme) {
    if (!this.authApi.doesUserHaveClaim(CLAIMS.CHANGE_CRLINC_THEME)) {
      console.warn(
        'Theme attempted to switch but user does not have permissions!'
      );
      return;
    }

    if (!theme) {
      console.error('Passed in theme does not exist!');
      return;
    }

    const htmlElement = this.document.querySelector('html')!;

    if (
      (htmlElement.classList.contains('crlinc-dark') && theme === 'light') ||
      (!htmlElement.classList.contains('crlinc-dark') && theme === 'dark')
    ) {
      htmlElement.classList.toggle('crlinc-dark');
    }
    // FullCalendar's classic palette uses this attribute for its dark colors.
    htmlElement.setAttribute('data-color-scheme', theme);
    this.currentTheme = theme;
    this.storageService.storeTheme(theme);
    this.themeChanged.emit(theme);
  }

  async tryRestoreThemeFromStorage() {
    let storedTheme = await this.storageService.getTheme();

    // Handle cases where the browser already has one of our old themes stored
    // resets them to their preferred version from the windows setting
    if (storedTheme !== 'light' && storedTheme !== 'dark') {
      this.storageService.storeTheme(null);
      storedTheme = null;
    }

    if (storedTheme) {
      // Use local storage theme first
      this.switchTheme(storedTheme);
    } else if (
      window.matchMedia &&
      window.matchMedia('(prefers-color-scheme: dark)').matches
    ) {
      // if their windows setting prefers dark mode, use that
      this.switchTheme('dark');
    } else {
      // otherwise show light mode
      this.switchTheme('light');
    }
  }

  getSelectedTheme(): AppTheme {
    return this.currentTheme;
  }
}
